import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const subMenuReveal = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -6px);
	}
	to {
		opacity: 1;
		transform: translate(-50%, 0);
	}
`;

interface HoverProps {
	isHover?: boolean;
	defaultColor?: string;
	isAuthPath?: boolean;
	isScrolled?: boolean;
	menuCount?: number;
	/** 히어로 상단 등 배경 완전 투명 — 반투명·블러 제거 */
	transparentBar?: boolean;
	/** 히어로 위 흰 메뉴 — 대비 보강 */
	onHero?: boolean;
	/** 모바일 전체 메뉴 열림 — 오버레이(99) 위에 햄버거/X 유지 */
	$menuOpen?: boolean;
}
interface ColorProps {
	defaultColor: string;
	/** 히어로 투명 헤더(밝은 배너 위 흰 글자) — 가독성 보강 */
	onHero?: boolean;
}
export const HeaderContainer = styled.header<HoverProps>`
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	overflow: visible;
	background: ${(props) => props.defaultColor};
	backdrop-filter: ${(props) => (props.transparentBar ? 'none' : 'blur(10px)')};
	-webkit-backdrop-filter: ${(props) => (props.transparentBar ? 'none' : 'blur(10px)')};
	height: ${(props) => (props.isHover ? `${props.theme.heights.headerHover}` : `${props.theme.heights.header}`)};
	z-index: ${(props) => (props.$menuOpen ? 110 : 10)};
	border-bottom: ${(props) =>
		props.isAuthPath ? `1px solid ${props.theme.colors.borderDefault}` : props.isScrolled ? `1px solid ${props.theme.colors.borderDefault}` : 'none'};
	transition:
		height ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard},
		background-color ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard},
		border-color ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard},
		backdrop-filter ${(props) => props.theme.motion.duration.slow} ${(props) => props.theme.motion.easing.standard};
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		height: ${(props) => props.theme.heights.headerMobile};
		background: ${(props) => props.defaultColor};
	}
`;
export const Nav = styled.nav<HoverProps>`
	display: flex;
	overflow: visible;
	/* 접힘(center) ↔ 펼침(flex-start) 전환 시 첫 행이 위로 튀는 현상 방지: 항상 상단 기준 + 동일 세로 패딩 */
	align-items: flex-start;
	justify-content: space-between;
	gap: 0 1rem;
	position: relative;
	max-width: 184rem;
	margin: 0 auto;
	height: ${(props) => (props.isHover ? `${props.theme.heights.headerHover}` : `${props.theme.heights.header}`)};
	padding: 0.25rem 2rem 0.4rem;
	min-width: 0;
	transition: height ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard};
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		height: ${(props) => props.theme.heights.headerMobile};
		align-items: center;
		padding: 0 2rem;
	}
`;
/** 로고·우측 인증 사이에서 GNB를 자연스럽게 가운데 정렬 (absolute 제거) */
export const NavCenter = styled.div`
	flex: 1;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	min-width: 0;
	padding: 0 0.8rem;
	overflow: visible;
`;
export const MenuList = styled.div<{ menuCount: number }>`
	position: relative;
	display: grid;
	grid-auto-flow: column;
	/* min 8.5rem × 7열은 좁은 데스크톱에서 로고·우측 영역과 수평 겹침 유발 → 0까지 수축 허용 */
	grid-template-columns: repeat(${(props) => props.menuCount}, minmax(0, 11rem));
	grid-auto-rows: auto;
	justify-items: stretch;
	align-items: start;
	column-gap: 0.35rem;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	overflow: visible;
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		display: none;
	}
`;
export const MenuColumn = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	min-width: 0;
	padding: 0 0.25rem;
	z-index: 1;
	&[data-state='open'] {
		z-index: 40;
	}
`;
export const SubMenuList = styled.div`
	position: absolute;
	left: 50%;
	top: 100%;
	transform: translate(-50%, 0);
	display: flex;
	flex-direction: column;
	align-items: stretch;
	min-width: min(32rem, calc(100vw - 4rem));
	max-width: min(40rem, calc(100vw - 3rem));
	width: max-content;
	margin-top: 0.4rem;
	padding: 0.6rem 0;
	background: ${(props) => props.theme.colors.surfacePage};
	border: 1px solid ${(props) => props.theme.colors.borderSubtle};
	border-radius: ${(props) => props.theme.borderRadius.md};
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.06),
		0 12px 32px -4px rgba(18, 83, 158, 0.12);
	gap: 0.2rem;
	animation: ${subMenuReveal} ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.decelerate}
		both;
`;
export const MenuItem = styled.div<HoverProps>`
	position: relative;
	color: ${(props) => props.defaultColor};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightMedium};
	line-height: 1.35;
	letter-spacing: -0.025em;
	white-space: nowrap;
	display: flex;
	min-height: 6.4rem;
	height: 6.4rem;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	flex-shrink: 0;
	transition: color ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard};
	${(props) =>
		props.onHero &&
		`
		&:not(.sub) {
			font-weight: ${props.theme.fontWeight.weightSemi};
		}
	`}
	&:not(.sub)::after {
		content: '';
		position: absolute;
		bottom: 1rem;
		left: 50%;
		width: 42%;
		height: 2px;
		border-radius: 1px;
		background: ${(props) => props.theme.colors.primary400};
		transform: translateX(-50%) scaleX(0);
		transform-origin: center;
		transition:
			transform ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard},
			background ${(props) => props.theme.motion.duration.normal} ${(props) => props.theme.motion.easing.standard};
		opacity: 0.95;
	}
	&:not(.sub):hover {
		color: ${(props) => props.theme.colors.primaryHover};
	}
	&:not(.sub):hover::after {
		transform: translateX(-50%) scaleX(1);
		background: ${(props) => props.theme.colors.primaryHover};
	}
	${(props) =>
		props.onHero &&
		`
		&:not(.sub)::after {
			background: rgba(255, 255, 255, 0.92);
		}
		&:not(.sub):hover {
			color: #fff;
			opacity: 0.95;
		}
		&:not(.sub):hover::after {
			background: rgba(255, 255, 255, 0.95);
		}
	`}
	&.sub {
		cursor: pointer;
		height: auto;
		min-height: 4.4rem;
		box-sizing: border-box;
		padding: 0.75rem 1.4rem;
		font-size: ${(props) => props.theme.fontSize.sizeXs};
		font-weight: ${(props) => props.theme.fontWeight.weightRegular};
		line-height: 1.5;
		letter-spacing: -0.015em;
		white-space: normal;
		word-break: keep-all;
		overflow-wrap: anywhere;
		text-align: left;
		justify-content: flex-start;
		align-items: center;
		color: ${(props) => props.defaultColor ?? props.theme.colors.textSecondary};
		border-radius: ${(props) => props.theme.borderRadius.sm};
		transition:
			color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard},
			background-color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard};
	}
	&.sub::after {
		display: none;
	}
	&.sub:hover {
		color: ${(props) => props.theme.colors.primary400};
		font-weight: ${(props) => props.theme.fontWeight.weightMedium};
		background-color: rgba(18, 83, 158, 0.06);
	}
`;
export const AuthBox = styled.div<{ isLogin: boolean }>`
	display: grid;
	grid-template-columns: ${(props) => (props.isLogin ? 'minmax(0, max-content)' : 'repeat(2, 1fr)')};
	grid-auto-rows: minmax(6.4rem, auto);
	gap: 2.4rem;
	justify-items: ${(props) => (props.isLogin ? 'end' : 'center')};
	align-items: center;
	flex-shrink: 0;

	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		grid-auto-rows: minmax(28px, auto);
		${(props) =>
			props.isLogin
				? `
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: flex-end;
			gap: 1.2rem;
			width: auto;
			min-width: 0;
		`
				: `
			grid-template-columns: 5.5rem 2.4rem;
			gap: 1.2rem;
		`}
	}
`;
export const AuthButton = styled.button<ColorProps>`
	background: transparent;
	border: none;
	color: ${(props) => props.defaultColor};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => (props.onHero ? props.theme.fontWeight.weightBold : props.theme.fontWeight.weightSemi)};
	line-height: 1.35;
	letter-spacing: -0.03em;
	cursor: pointer;
	transition:
		color ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard},
		opacity ${(props) => props.theme.motion.duration.fast} ${(props) => props.theme.motion.easing.standard};
	${(props) =>
		props.onHero
			? `
		:hover {
			opacity: 0.92;
		}
	`
			: `
		:hover {
			color: ${props.theme.colors.primaryHover};
			opacity: 1;
		}
	`}
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		font-size: ${(props) => props.theme.fontSize.sizeSm};
		font-weight: ${(props) => props.theme.fontWeight.weightBold};
		line-height: 1.2;
	}
`;
export const UserMenuRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.8rem;
	min-height: 6.4rem;
	flex-shrink: 0;
	flex-wrap: nowrap;
	justify-content: flex-end;
	max-width: 52rem;
`;
export const UserNameButton = styled(AuthButton)`
	max-width: 18rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	padding: 0;
	text-align: left;
`;
export const UserMenuDivider = styled.span<{ defaultColor: string }>`
	display: inline-block;
	width: 3px;
	height: 3px;
	border-radius: 50%;
	background: ${(props) => props.defaultColor};
	opacity: 0.38;
	flex-shrink: 0;
	user-select: none;
`;
export const LogoBox = styled.div`
	display: flex;
	align-items: center;
	min-height: 0;
	flex-shrink: 0;
`;
export const ImgBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 20rem;
	height: 6.4rem;
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		width: 16.5rem;
		height: 4.95rem;
	}
	img {
		display: block;
		width: 100%;
		height: auto;
		max-height: 100%;
		object-fit: contain;
		object-position: left center;
	}
`;
